import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { CasdoorAuthService } from 'app/core/auth/casdoor-auth.service';
import { Capacitor } from '@capacitor/core';
import { compressImage, sanitizeFileName } from 'app/modules/dashboard/file-manager/file-utils';
import { cloneDeep } from 'lodash';
import { environment } from 'environments/environment';
import { MedicalOpinion } from '../../expert/shared/models/medical-opinion.models';
import { IMedicalOpinion } from '../../expert/shared/types/medical-opinion.types';
import { UserProfileService } from '../../user-profile/user-profile.service';
import { PatientCacheService } from '../patient/patient-cache.services';
import { PatientService } from '../patient/patient.service';
import { MedicalRequest } from './request.models';
import {
    convertPageToPagination,
    ECGRequestForm,
    IDermatologyRequestSave,
    IECGRequestCreate,
    IECGRequestUpdate,
    IMedicalRequest,
    IRequestFilter,
    IRequestListResponse,
    IRequestPagination,
    IRequestStats
} from './request.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FCMService } from 'app/core/fcm/fcm.service';
import { AppActivityService } from 'app/core/app-activity.service';

/**
 * Request service - updated with new unified endpoints from swagger
 * Now uses UserService to get ownerId automatically
 */
@Injectable({ providedIn: 'root' })
export class RequestService {
    private _httpClient = inject(HttpClient);
    private _authService = inject(CasdoorAuthService);
    private _userProfileService = inject(UserProfileService);   
    private _patientService = inject(PatientService);
    private _patientCacheService = inject(PatientCacheService);
    private _fcmService = inject(FCMService);
    private _appActivity = inject(AppActivityService);

    // Private signals for state management
    private _allRequests = signal<MedicalRequest[]>([]);
    private _currentRequest = signal<MedicalRequest | null>(null);
    private _isLoading = signal<boolean>(false);
    private _error = signal<string | null>(null);
    private _currentFilter = signal<IRequestFilter>({ sort: 'zcreationTimestamp:desc' });
    private _pagination = signal<IRequestPagination | null>(null);

    // Medical opinions signals
    private _currentRequestOpinions = signal<MedicalOpinion[]>([]);
    private _opinionsLoading = signal<boolean>(false);
    private _opinionsError = signal<string | null>(null);

    // Public readonly signals
    public readonly requests = this._allRequests.asReadonly();
    public readonly currentRequest = this._currentRequest.asReadonly();
    public readonly isLoading = this._isLoading.asReadonly();
    public readonly error = this._error.asReadonly();
    public readonly currentFilter = this._currentFilter.asReadonly();
    public readonly pagination = this._pagination.asReadonly();
    public readonly currentRequestOpinions = this._currentRequestOpinions.asReadonly();
    public readonly opinionsLoading = this._opinionsLoading.asReadonly();
    public readonly opinionsError = this._opinionsError.asReadonly();

    /**
     * Computed signal for request statistics
     */
    public readonly requestStats = computed(() => {
        const requests = this._allRequests();
        const stats: IRequestStats = {
            totalRequests: requests.length,
            draftCount: requests.filter(r => r.status === 'DRAFT').length,
            waitingCount: requests.filter(r => r.status === 'WAITING').length,
            inProgressCount: requests.filter(r => r.status === 'IN_PROGRESS').length,
            completedCount: requests.filter(r => r.status === 'COMPLETED').length,
            archivedCount: requests.filter(r => r.status === 'ARCHIVED').length,
            bySpeciality: {},
            byRequestType: {}
        };

        requests.forEach(request => {
            stats.bySpeciality[request.speciality] = (stats.bySpeciality[request.speciality] || 0) + 1;
            stats.byRequestType[request.requestType] = (stats.byRequestType[request.requestType] || 0) + 1;
        });

        return stats;
    });

    public readonly hasRequests = computed(() => this._allRequests().length > 0);
    public readonly hasUnreadOpinions = computed(() => {
        const currentRequest = this._currentRequest();
        return currentRequest ? !currentRequest.isRead && currentRequest.status === 'COMPLETED' : false;
    });

    constructor() {
        this.loadRequests({ sort: 'zcreationTimestamp:desc' });

        // Subscribe to FCM notifications to auto-refresh the requests list
        this._fcmService.onMessageReceived$
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                console.log('[RequestService] FCM message received, reloading requests...');
                // Silently reload requests to maintain the current filter
                this.loadRequests(this._currentFilter());
            });

        // A push can be dropped, delayed, or declined, and none of that should leave a request
        // showing a status it no longer has. Coming back to the app re-reads the truth.
        this._appActivity.resumed$
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.loadRequests(this._currentFilter());
            });
    }

    /**
     * True on any native platform (iOS or Android). CapacitorHttp - used by the auth
     * interceptor's native bypass on both platforms - only accepts string/JSON bodies
     * (per Capacitor's own docs), not FormData, so file uploads must go through the
     * base64-JSON path here instead of multipart FormData.
     */
    private usesNativeHttpBridge(): boolean {
        return Capacitor.isNativePlatform();
    }

    /**
     * Convert File to base64 string
     */
    private async fileToBase64(file: File): Promise<string> {
        const startTime = performance.now();
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        console.log(`[PERF] fileToBase64 START - File: ${file.name}, Size: ${fileSizeMB}MB`);
        
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const conversionTime = performance.now() - startTime;
                const result = reader.result as string;
                // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
                const base64 = result.split(',')[1];
                const base64SizeMB = (base64.length * 3 / 4 / (1024 * 1024)).toFixed(2);
                console.log(`[PERF] fileToBase64 END - Conversion time: ${conversionTime.toFixed(2)}ms, Base64 size: ${base64SizeMB}MB`);
                resolve(base64);
            };
            reader.onerror = (error) => {
                const conversionTime = performance.now() - startTime;
                console.error(`[PERF] fileToBase64 ERROR - Failed after ${conversionTime.toFixed(2)}ms:`, error);
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    }

    /**
     * Download file by fileId and convert to base64
     */
    private async fileIdToBase64(fileId: string): Promise<{ base64: string; fileName: string; fileType: string }> {
        const totalStartTime = performance.now();
        console.log(`[PERF] fileIdToBase64 START - FileId: ${fileId}`);
        
        try {
            // Download file as blob
            const downloadStartTime = performance.now();
            const response = await this._httpClient
                .get(`aplt_backend/api/apltfiles/files/${fileId}/download`, {
                    responseType: 'blob',
                    observe: 'response'
                })
                .toPromise();

            const downloadTime = performance.now() - downloadStartTime;
            
            if (!response || !response.body) {
                throw new Error('No file content received from server');
            }

            const blob = response.body;
            const blobSizeMB = (blob.size / (1024 * 1024)).toFixed(2);
            console.log(`[PERF] fileIdToBase64 - Download time: ${downloadTime.toFixed(2)}ms, Blob size: ${blobSizeMB}MB`);
            
            // Extract filename from Content-Disposition header or use default
            let fileName = `file-${fileId.substring(0, 8)}`;
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/['"]/g, '');
                }
            }

            // Convert blob to base64
            const conversionStartTime = performance.now();
            const base64 = await this.blobToBase64(blob);
            const conversionTime = performance.now() - conversionStartTime;
            const base64SizeMB = (base64.length * 3 / 4 / (1024 * 1024)).toFixed(2);
            console.log(`[PERF] fileIdToBase64 - Conversion time: ${conversionTime.toFixed(2)}ms, Base64 size: ${base64SizeMB}MB`);
            
            const totalTime = performance.now() - totalStartTime;
            console.log(`[PERF] fileIdToBase64 END - Total time: ${totalTime.toFixed(2)}ms (Download: ${downloadTime.toFixed(2)}ms, Conversion: ${conversionTime.toFixed(2)}ms)`);
            
            return {
                base64,
                fileName: sanitizeFileName(fileName),
                fileType: blob.type || 'application/octet-stream'
            };
        } catch (error) {
            const totalTime = performance.now() - totalStartTime;
            console.error(`[PERF] fileIdToBase64 ERROR - Failed after ${totalTime.toFixed(2)}ms:`, error);
            throw error;
        }
    }

    /**
     * Convert Blob to base64 string
     */
    private async blobToBase64(blob: Blob): Promise<string> {
        const startTime = performance.now();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const conversionTime = performance.now() - startTime;
                const result = reader.result as string;
                // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
                const base64 = result.split(',')[1];
                console.log(`[PERF] blobToBase64 - Conversion time: ${conversionTime.toFixed(2)}ms`);
                resolve(base64);
            };
            reader.onerror = (error) => {
                const conversionTime = performance.now() - startTime;
                console.error(`[PERF] blobToBase64 ERROR - Failed after ${conversionTime.toFixed(2)}ms:`, error);
                reject(error);
            };
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Get current clinician entityId for ownerId parameter from the clinicial Profile
     */
    private async getOwnerId(): Promise<string> {
        // Try to get from already loaded profile first
        const clinicianProfile = this._userProfileService.clinicianProfile();
        if (clinicianProfile?.entityId) {
            return clinicianProfile.entityId;
        }

        // If not loaded, fetch the profile
        try {
            const profile = await this._userProfileService.getProfile();
            if (profile?.entityId) {
                return profile.entityId;
            }
        } catch (error) {
            console.warn('Could not load clinician profile:', error);
        }

        throw new Error('Owner ID not available - clinician profile not loaded');
    }


    /**
     * Load requests using new unified endpoint
     */
    async loadRequests(filter: IRequestFilter = {}): Promise<void> {
        const mergedFilter = { sort: 'zcreationTimestamp:desc', ...filter };
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const ownerId = await this.getOwnerId();
            let params = new HttpParams().set('ownerId', ownerId);
            
            if (mergedFilter.search) params = params.set('search', mergedFilter.search);
            if (mergedFilter.f_status) params = params.set('f_status', mergedFilter.f_status);
            if (mergedFilter.f_speciality) params = params.set('expertisearea', mergedFilter.f_speciality);
            if (mergedFilter.f_request_type) params = params.set('f_request_type', mergedFilter.f_request_type);
            if (mergedFilter.f_reading !== undefined) params = params.set('f_reading', mergedFilter.f_reading.toString());
            if (mergedFilter.sort) params = params.set('sort', mergedFilter.sort);
            if (mergedFilter.page !== undefined) params = params.set('page', mergedFilter.page.toString());
            if (mergedFilter.per_page !== undefined) params = params.set('per_page', mergedFilter.per_page.toString());

            const response = await this._httpClient
                .get<IRequestListResponse>('aplt_backend/api/requests', { params })
                .toPromise();

            if (response) {
                const requests = response.content?.map(item => new MedicalRequest(item)) || [];
                const requestsWithPatients = await Promise.all(
                    requests.map(request => this.loadPatientDataForRequest(request))
                );
                
                this._allRequests.set(requestsWithPatients);
                const pagination = convertPageToPagination(response);
                this._pagination.set(pagination);
                this._currentFilter.set(mergedFilter);
            }
        } catch (error: any) {
            // Silently handle authentication errors - interceptor will redirect to sign-in
            const isAuthError = error?.error === 'missing_refresh_token' || 
                               error?.error_description?.includes('Missing Refresh Token') ||
                               error?.status === 401;
            
            if (!isAuthError) {
                console.error('Error loading requests:', error);
            }
            // this._error.set(error instanceof Error ? error.message : 'Failed to load requests');
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Get request by ID using new unified endpoint
     */
    async getRequestById(id: string, forceReload: boolean = false): Promise<MedicalRequest | null> {
        const existingRequest = this._allRequests().find(r => r.id === id || r.entityId === id);
        if (existingRequest && !forceReload) {
            this._currentRequest.set(cloneDeep(existingRequest));
            return existingRequest;
        }

        this._isLoading.set(true);
        this._error.set(null);

        try {
            const ownerId = await this.getOwnerId();
            let params = new HttpParams().set('ownerId', ownerId);

            const response = await this._httpClient
                .get<IMedicalRequest>(`aplt_backend/api/requests/${id}`, { params })
                .toPromise();

            if (response) {
                const request = new MedicalRequest(response);
                const requestWithPatient = await this.loadPatientDataForRequest(request);
                this._currentRequest.set(cloneDeep(requestWithPatient));

                // Update in the list if exists
                const requests = this._allRequests();
                const updatedRequests = requests.map(r => {
                    if (r.id === id || r.entityId === id) {
                        return requestWithPatient;
                    }
                    return r;
                });
                this._allRequests.set(updatedRequests);

                return requestWithPatient;
            }
            return null;
        } catch (error) {
            console.error('Error loading request:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to load request');
            return null;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * iOS: Uses JSON with base64 encoded file
     * Android/Web: Uses FormData (existing behavior)
     */
    async createECGRequest(requestData: IECGRequestCreate, file?: File): Promise<MedicalRequest> {
        const totalStartTime = performance.now();
        console.log(`[PERF] createECGRequest START - Platform: ${this.usesNativeHttpBridge() ? 'Native' : 'Web'}, HasFile: ${!!file}`);
        
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const useNativeUpload = this.usesNativeHttpBridge();

            if (useNativeUpload && file) {
                // iOS: Compress image (if applicable) then convert to base64 and send as JSON
                const sanitizedFileName = sanitizeFileName(file.name);
                
                // Compress image before conversion (reduces payload size)
                let fileToProcess = file;
                if (file.type.startsWith('image/')) {
                    try {
                        const compressionStartTime = performance.now();
                        fileToProcess = await compressImage(file, {
                            quality: 0.8,
                            maxWidth: 1920,
                            maxHeight: 1920,
                            maxSizeBytes: 2 * 1024 * 1024, // 2MB
                            outputType: 'image/jpeg'
                        });
                        const compressionTime = performance.now() - compressionStartTime;
                        console.log(`[PERF] createECGRequest iOS - Image compression: ${compressionTime.toFixed(2)}ms`);
                    } catch (error) {
                        console.warn('[PERF] createECGRequest iOS - Image compression failed, using original file:', error);
                        // Continue with original file if compression fails
                    }
                }
                
                const conversionStartTime = performance.now();
                const base64File = await this.fileToBase64(fileToProcess);
                const conversionTime = performance.now() - conversionStartTime;
                console.log(`[PERF] createECGRequest iOS - Base64 conversion: ${conversionTime.toFixed(2)}ms`);

                const jsonBody: ECGRequestForm = {
                    message: requestData.message,
                    expertiseArea: requestData.speciality,
                    requestType: requestData.requestType,
                    patientId: requestData.patientId,
                    fileB64: base64File,
                    fileName: sanitizedFileName,
                    fileType: file.type,
                    personalHistory: requestData.personalHistory,
                    familyHistory: requestData.familyHistory,
                    cardiovascularRiskFactors: requestData.cardiovascularRiskFactors,
                    regularMedications: requestData.regularMedications,
                    functionalSymptoms: requestData.functionalSymptoms,
                    clinicalSigns: requestData.clinicalSigns
                };

                const jsonSizeMB = (JSON.stringify(jsonBody).length / (1024 * 1024)).toFixed(2);
                console.log(`[PERF] createECGRequest iOS - JSON payload size: ${jsonSizeMB}MB`);

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });

                const requestStartTime = performance.now();
                const response = await this._httpClient
                    .post<IMedicalRequest>('aplt_backend/api/requests/ECG', jsonBody, { headers })
                    .toPromise();
                const requestTime = performance.now() - requestStartTime;
                console.log(`[PERF] createECGRequest iOS - Backend request time: ${requestTime.toFixed(2)}ms`);
                
                if (!response) throw new Error('No response received from server');

                const newRequest = new MedicalRequest(response);
                
                const reloadStartTime = performance.now();
                await this.loadRequests(this._currentFilter());
                const reloadTime = performance.now() - reloadStartTime;
                console.log(`[PERF] createECGRequest iOS - List reload time: ${reloadTime.toFixed(2)}ms`);
                
                const totalTime = performance.now() - totalStartTime;
                console.log(`[PERF] createECGRequest iOS END - Total time: ${totalTime.toFixed(2)}ms (Conversion: ${conversionTime.toFixed(2)}ms, Backend: ${requestTime.toFixed(2)}ms, Reload: ${reloadTime.toFixed(2)}ms)`);
                
                return newRequest;
            } else {
                // Android/Web: Use FormData (existing behavior)
                const formDataStartTime = performance.now();
                const formData = new FormData();
                if (file) {
                    const sanitizedFile = new File([file], sanitizeFileName(file.name), { type: file.type });
                    formData.append('file', sanitizedFile);
                }

                let params = new HttpParams()
                    .set('message', requestData.message)
                    .set('speciality', requestData.speciality)
                    .set('requestType', requestData.requestType)
                    .set('patientId', requestData.patientId);

                // Add optional fields if provided
                if (requestData.personalHistory) params = params.set('personalHistory', requestData.personalHistory);
                if (requestData.familyHistory) params = params.set('familyHistory', requestData.familyHistory);
                if (requestData.cardiovascularRiskFactors) params = params.set('cardiovascularRiskFactors', requestData.cardiovascularRiskFactors);
                if (requestData.regularMedications) params = params.set('regularMedications', requestData.regularMedications);
                if (requestData.functionalSymptoms) params = params.set('functionalSymptoms', requestData.functionalSymptoms);
                if (requestData.clinicalSigns) params = params.set('clinicalSigns', requestData.clinicalSigns);

                const formDataTime = performance.now() - formDataStartTime;
                console.log(`[PERF] createECGRequest Android/Web - FormData preparation: ${formDataTime.toFixed(2)}ms`);

                // Use HttpClient - the interceptor handles FormData Content-Type correctly
                const requestStartTime = performance.now();
                const response = await this._httpClient
                    .post<IMedicalRequest>('aplt_backend/api/requests/ECG', formData, { params })
                    .toPromise();
                const requestTime = performance.now() - requestStartTime;
                console.log(`[PERF] createECGRequest Android/Web - Backend request time: ${requestTime.toFixed(2)}ms`);
                
                if (!response) throw new Error('No response received from server');

                const newRequest = new MedicalRequest(response);
                
                const reloadStartTime = performance.now();
                await this.loadRequests(this._currentFilter());
                const reloadTime = performance.now() - reloadStartTime;
                console.log(`[PERF] createECGRequest Android/Web - List reload time: ${reloadTime.toFixed(2)}ms`);
                
                const totalTime = performance.now() - totalStartTime;
                console.log(`[PERF] createECGRequest Android/Web END - Total time: ${totalTime.toFixed(2)}ms (FormData: ${formDataTime.toFixed(2)}ms, Backend: ${requestTime.toFixed(2)}ms, Reload: ${reloadTime.toFixed(2)}ms)`);
                
                return newRequest;
            }
        } catch (error) {
            const totalTime = performance.now() - totalStartTime;
            console.error(`[PERF] createECGRequest ERROR - Failed after ${totalTime.toFixed(2)}ms:`, error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to create request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * iOS: Uses JSON with base64 encoded file
     * Android/Web: Uses FormData (existing behavior)
     */
    async updateECGRequest(id: string, requestData: IECGRequestUpdate, file?: File): Promise<MedicalRequest> {
        const totalStartTime = performance.now();
        console.log(`[PERF] updateECGRequest START - RequestId: ${id}, Platform: ${this.usesNativeHttpBridge() ? 'Native' : 'Web'}, HasFile: ${!!file}`);
        
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const useNativeUpload = this.usesNativeHttpBridge();

            if (useNativeUpload && file) {
                // iOS: Compress image (if applicable) then convert to base64 and send as JSON
                const sanitizedFileName = sanitizeFileName(file.name);
                
                // Compress image before conversion (reduces payload size)
                let fileToProcess = file;
                if (file.type.startsWith('image/')) {
                    try {
                        const compressionStartTime = performance.now();
                        fileToProcess = await compressImage(file, {
                            quality: 0.8,
                            maxWidth: 1920,
                            maxHeight: 1920,
                            maxSizeBytes: 2 * 1024 * 1024, // 2MB
                            outputType: 'image/jpeg'
                        });
                        const compressionTime = performance.now() - compressionStartTime;
                        console.log(`[PERF] updateECGRequest iOS - Image compression: ${compressionTime.toFixed(2)}ms`);
                    } catch (error) {
                        console.warn('[PERF] updateECGRequest iOS - Image compression failed, using original file:', error);
                        // Continue with original file if compression fails
                    }
                }
                
                const conversionStartTime = performance.now();
                const base64File = await this.fileToBase64(fileToProcess);
                const conversionTime = performance.now() - conversionStartTime;
                console.log(`[PERF] updateECGRequest iOS - Base64 conversion: ${conversionTime.toFixed(2)}ms`);

                const jsonBody: ECGRequestForm = {
                    message: requestData.message,
                    expertiseArea: requestData.speciality,
                    requestType: requestData.requestType,
                    patientId: requestData.patientId,
                    fileB64: base64File,
                    fileName: sanitizedFileName,
                    fileType: file.type,
                    personalHistory: requestData.personalHistory,
                    familyHistory: requestData.familyHistory,
                    cardiovascularRiskFactors: requestData.cardiovascularRiskFactors,
                    regularMedications: requestData.regularMedications,
                    functionalSymptoms: requestData.functionalSymptoms,
                    clinicalSigns: requestData.clinicalSigns
                };

                const jsonSizeMB = (JSON.stringify(jsonBody).length / (1024 * 1024)).toFixed(2);
                console.log(`[PERF] updateECGRequest iOS - JSON payload size: ${jsonSizeMB}MB`);

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });

                const requestStartTime = performance.now();
                const response = await this._httpClient
                    .put<IMedicalRequest>(`aplt_backend/api/requests/ECG/${id}`, jsonBody, { headers })
                    .toPromise();
                const requestTime = performance.now() - requestStartTime;
                console.log(`[PERF] updateECGRequest iOS - Backend request time: ${requestTime.toFixed(2)}ms`);
                
                if (!response) throw new Error('No response received from server');

                const updatedRequest = new MedicalRequest(response);
                // OPTIMIZATION: Use cached patient data if available (faster)
                const currentRequest = this._allRequests().find(r => r.id === id || r.entityId === id);
                let updatedRequestWithPatient: MedicalRequest;
                if (updatedRequest.patient || !currentRequest?.patient) {
                    updatedRequestWithPatient = await this.loadPatientDataForRequest(updatedRequest);
                } else {
                    const cloned = updatedRequest.clone();
                    cloned.patient = currentRequest.patient;
                    updatedRequestWithPatient = cloned;
                }
                
                // IMMEDIATELY update the list optimistically (don't wait for reload)
                const requests = this._allRequests();
                const updatedRequests = requests.map(r => {
                    if (r.id === id || r.entityId === id) {
                        return updatedRequestWithPatient;
                    }
                    return r;
                });
                this._allRequests.set(updatedRequests);
                
                if (this._currentRequest()?.id === id || this._currentRequest()?.entityId === id) {
                    this._currentRequest.set(cloneDeep(updatedRequestWithPatient));
                }
                
                // Reload list in background without blocking (fire and forget)
                this.loadRequests(this._currentFilter()).catch(error => {
                    console.warn('Background reload after draft update failed:', error);
                });
                
                return updatedRequestWithPatient;
            } else {
                // Android/Web: Use FormData (existing behavior)
                const formDataStartTime = performance.now();
                const formData = new FormData();
                if (file) {
                    const sanitizedFile = new File([file], sanitizeFileName(file.name), { type: file.type });
                    formData.append('file', sanitizedFile);
                }

                let params = new HttpParams()
                    .set('message', requestData.message)
                    .set('speciality', requestData.speciality)
                    .set('requestType', requestData.requestType)
                    .set('patientId', requestData.patientId);

                // Add optional fields if provided
                if (requestData.personalHistory) params = params.set('personalHistory', requestData.personalHistory);
                if (requestData.familyHistory) params = params.set('familyHistory', requestData.familyHistory);
                if (requestData.cardiovascularRiskFactors) params = params.set('cardiovascularRiskFactors', requestData.cardiovascularRiskFactors);
                if (requestData.regularMedications) params = params.set('regularMedications', requestData.regularMedications);
                if (requestData.functionalSymptoms) params = params.set('functionalSymptoms', requestData.functionalSymptoms);
                if (requestData.clinicalSigns) params = params.set('clinicalSigns', requestData.clinicalSigns);

                const formDataTime = performance.now() - formDataStartTime;
                console.log(`[PERF] updateECGRequest Android/Web - FormData preparation: ${formDataTime.toFixed(2)}ms`);

                // Use HttpClient - the interceptor handles FormData Content-Type correctly
                const requestStartTime = performance.now();
                const response = await this._httpClient
                    .put<IMedicalRequest>(`aplt_backend/api/requests/ECG/${id}`, formData, { params })
                    .toPromise();
                const requestTime = performance.now() - requestStartTime;
                console.log(`[PERF] updateECGRequest Android/Web - Backend request time: ${requestTime.toFixed(2)}ms`);
                
                if (!response) throw new Error('No response received from server');

                const updatedRequest = new MedicalRequest(response);
                
                const patientDataStartTime = performance.now();
                // OPTIMIZATION: Use cached patient data if available (faster)
                const currentRequest = this._allRequests().find(r => r.id === id || r.entityId === id);
                let updatedRequestWithPatient: MedicalRequest;
                if (updatedRequest.patient || !currentRequest?.patient) {
                    updatedRequestWithPatient = await this.loadPatientDataForRequest(updatedRequest);
                } else {
                    const cloned = updatedRequest.clone();
                    cloned.patient = currentRequest.patient;
                    updatedRequestWithPatient = cloned;
                }
                const patientDataTime = performance.now() - patientDataStartTime;
                console.log(`[PERF] updateECGRequest Android/Web - Patient data load: ${patientDataTime.toFixed(2)}ms`);
                
                // IMMEDIATELY update the list optimistically (don't wait for reload)
                const requests = this._allRequests();
                const updatedRequests = requests.map(r => {
                    if (r.id === id || r.entityId === id) {
                        return updatedRequestWithPatient;
                    }
                    return r;
                });
                this._allRequests.set(updatedRequests);
                
                if (this._currentRequest()?.id === id || this._currentRequest()?.entityId === id) {
                    this._currentRequest.set(cloneDeep(updatedRequestWithPatient));
                }
                
                // Reload list in background without blocking (fire and forget)
                this.loadRequests(this._currentFilter()).catch(error => {
                    console.warn('Background reload after draft update failed:', error);
                });
                
                const totalTime = performance.now() - totalStartTime;
                console.log(`[PERF] updateECGRequest Android/Web END - Total time: ${totalTime.toFixed(2)}ms (FormData: ${formDataTime.toFixed(2)}ms, Backend: ${requestTime.toFixed(2)}ms, Patient: ${patientDataTime.toFixed(2)}ms)`);
                
                return updatedRequestWithPatient;
            }
        } catch (error) {
            const totalTime = performance.now() - totalStartTime;
            console.error(`[PERF] updateECGRequest ERROR - Failed after ${totalTime.toFixed(2)}ms:`, error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to update request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Create (id absent) or update a dermatology request, as a draft or sent to the experts.
     * Photos are compressed first: up to 10 can go in one call, and the payload must stay under
     * the server limit (20 MB). Native apps send base64 JSON, the web sends multipart, as for ECG.
     */
    async saveDermatologyRequest(
        data: IDermatologyRequestSave,
        options: { id?: string; send: boolean }
    ): Promise<MedicalRequest> {
        this._isLoading.set(true);
        try {
            const files = await Promise.all(data.files.map((file) => this.prepareDermatologyFile(file)));
            const url = options.id
                ? `aplt_backend/api/requests/DERMATOLOGY/${options.id}`
                : 'aplt_backend/api/requests/DERMATOLOGY';
            let params = new HttpParams().set('send', String(options.send));

            let response: IMedicalRequest | undefined;
            if (this.usesNativeHttpBridge()) {
                const body = {
                    message: data.message,
                    patientId: data.patientId,
                    files: await Promise.all(
                        files.map(async (file) => ({ fileB64: await this.fileToBase64(file), fileName: file.name }))
                    ),
                    removedFileIds: data.removedFileIds ?? [],
                };
                const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
                response = options.id
                    ? await this._httpClient.put<IMedicalRequest>(url, body, { headers, params }).toPromise()
                    : await this._httpClient.post<IMedicalRequest>(url, body, { headers, params }).toPromise();
            } else {
                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));
                params = params.set('message', data.message).set('patientId', data.patientId);
                (data.removedFileIds ?? []).forEach((fileId) => (params = params.append('removedFileIds', fileId)));
                response = options.id
                    ? await this._httpClient.put<IMedicalRequest>(url, formData, { params }).toPromise()
                    : await this._httpClient.post<IMedicalRequest>(url, formData, { params }).toPromise();
            }

            if (!response) throw new Error('No response received from server');
            const saved = await this.loadPatientDataForRequest(new MedicalRequest(response));
            if (this._currentRequest()?.id === saved.id) {
                this._currentRequest.set(cloneDeep(saved));
            }
            await this.loadRequests(this._currentFilter());
            return saved;
        } finally {
            this._isLoading.set(false);
        }
    }

    private async prepareDermatologyFile(file: File): Promise<File> {
        let prepared = file;
        if (file.type.startsWith('image/') && !file.name.toLowerCase().endsWith('.dcm')) {
            try {
                prepared = await compressImage(file, {
                    quality: 0.8,
                    maxWidth: 1920,
                    maxHeight: 1920,
                    maxSizeBytes: 1024 * 1024,
                    outputType: 'image/jpeg',
                });
            } catch (error) {
                console.warn('Image compression failed, using original file:', error);
            }
        }
        return new File([prepared], sanitizeFileName(prepared.name), { type: prepared.type });
    }

    /**
     * Archive request using new endpoint
     */
    async archiveRequest(id: string): Promise<MedicalRequest> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const response = await this._httpClient
                .post<IMedicalRequest>(`aplt_backend/api/requests/${id}/archive`, {})
                .toPromise();

            if (!response) throw new Error('No response received from server');

            const archivedRequest = new MedicalRequest(response);
            if (this._currentRequest()?.id === id) {
                this._currentRequest.set(cloneDeep(archivedRequest));
            }
            await this.loadRequests(this._currentFilter());
            return archivedRequest;
        } catch (error) {
            console.error('Error archiving request:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to archive request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Load medical opinions using new unified endpoint
     */
    async loadMedicalOpinions(requestId: string): Promise<void> {
        this._opinionsLoading.set(true);
        this._opinionsError.set(null);

        try {
            const response = await this._httpClient
                .get<IMedicalOpinion[]>(`aplt_backend/api/requests/${requestId}/medical-opinions`)
                .toPromise();

            if (response) {
                const opinions = response.map(item => new MedicalOpinion(item));
                opinions.sort((a, b) => new Date(b.zcreationTimestamp).getTime() - new Date(a.zcreationTimestamp).getTime());
                this._currentRequestOpinions.set(opinions);
            } else {
                this._currentRequestOpinions.set([]);
            }
        } catch (error) {
            console.error('Error loading medical opinions:', error);
            // this._opinionsError.set(error instanceof Error ? error.message : 'Failed to load medical opinions');
            this._currentRequestOpinions.set([]);
        } finally {
            this._opinionsLoading.set(false);
        }
    }

    /**
     * Mark request as read using swagger endpoint
     * POST /api/requests/{requestId}/read
     * Requires clinicianId as query parameter (the clinicianId FROM the request, not the current user)
     */
    async markRequestAsRead(requestId: string): Promise<void> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            // Get the request to extract its clinicianId
            const request = this._allRequests().find(r => r.id === requestId) || this._currentRequest();

            if (!request || !request.clinicianId) {
                throw new Error('Request or clinicianId not found');
            }

            let params = new HttpParams().set('clinicianId', request.clinicianId);

            await this._httpClient
                .post(`aplt_backend/api/requests/${requestId}/read`, {}, { params })
                .toPromise();

            // Update the current request if it matches
            const currentRequest = this._currentRequest();
            if (currentRequest && currentRequest.id === requestId) {
                const updatedRequest = currentRequest.clone();
                updatedRequest.isRead = true;
                this._currentRequest.set(updatedRequest);
            }

            // Update in the requests list
            const requests = this._allRequests();
            const updatedRequests = requests.map(request => {
                if (request.id === requestId) {
                    const updated = request.clone();
                    updated.isRead = true;
                    return updated;
                }
                return request;
            });
            this._allRequests.set(updatedRequests);

        } catch (error) {
            console.error('Error marking request as read:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to mark request as read');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Create and send ECG request immediately
     * iOS: Uses JSON with base64 encoded file
     * Android/Web: Uses FormData (existing behavior)
     */
    async createAndSendECGRequest(requestData: IECGRequestCreate, file?: File): Promise<MedicalRequest> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const useNativeUpload = this.usesNativeHttpBridge();

            if (useNativeUpload && file) {
                // iOS: Convert file to base64 and send as JSON
                const sanitizedFileName = sanitizeFileName(file.name);
                const base64File = await this.fileToBase64(file);

                const jsonBody: ECGRequestForm = {
                    message: requestData.message,
                    expertiseArea: requestData.speciality, // Backend expects expertiseArea (camelCase)
                    requestType: requestData.requestType,
                    patientId: requestData.patientId,
                    fileB64: base64File, // Backend expects fileB64, not file
                    fileName: sanitizedFileName,
                    fileType: file.type,
                    personalHistory: requestData.personalHistory,
                    familyHistory: requestData.familyHistory,
                    cardiovascularRiskFactors: requestData.cardiovascularRiskFactors,
                    regularMedications: requestData.regularMedications,
                    functionalSymptoms: requestData.functionalSymptoms,
                    clinicalSigns: requestData.clinicalSigns
                };

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });
                // Backend reads `send` as a query param (@RequestParam), not from the JSON body
                const params = new HttpParams().set('send', 'true');

                const response = await this._httpClient
                    .post<IMedicalRequest>('aplt_backend/api/requests/ECG', jsonBody, { headers, params })
                    .toPromise();
                if (!response) throw new Error('No response received from server');

                const sentRequest = new MedicalRequest(response);
                await this.loadRequests(this._currentFilter());
                return sentRequest;
            } else {
                // Android/Web: Use FormData (existing behavior)
                const formData = new FormData();
                if (file) {
                    const sanitizedFile = new File([file], sanitizeFileName(file.name), { type: file.type });
                    formData.append('file', sanitizedFile);
                }

                let params = new HttpParams()
                    .set('message', requestData.message)
                    .set('speciality', requestData.speciality)
                    .set('requestType', requestData.requestType)
                    .set('patientId', requestData.patientId)
                    .set('send', 'true'); // Send immediately

                // Add optional fields if provided
                if (requestData.personalHistory) params = params.set('personalHistory', requestData.personalHistory);
                if (requestData.familyHistory) params = params.set('familyHistory', requestData.familyHistory);
                if (requestData.cardiovascularRiskFactors) params = params.set('cardiovascularRiskFactors', requestData.cardiovascularRiskFactors);
                if (requestData.regularMedications) params = params.set('regularMedications', requestData.regularMedications);
                if (requestData.functionalSymptoms) params = params.set('functionalSymptoms', requestData.functionalSymptoms);
                if (requestData.clinicalSigns) params = params.set('clinicalSigns', requestData.clinicalSigns);

                // Use HttpClient - the interceptor handles FormData Content-Type correctly
                const response = await this._httpClient
                    .post<IMedicalRequest>('aplt_backend/api/requests/ECG', formData, { 
                        params
                    })
                    .toPromise();
                if (!response) throw new Error('No response received from server');

                const sentRequest = new MedicalRequest(response);
                await this.loadRequests(this._currentFilter());
                return sentRequest;
            }
        } catch (error) {
            console.error('Error creating and sending ECG request:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to create and send request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Update and send ECG request immediately
     * iOS: Uses JSON with base64 encoded file
     * Android/Web: Uses FormData (existing behavior)
     */
    async updateAndSendECGRequest(id: string, requestData: IECGRequestUpdate, file?: File): Promise<MedicalRequest> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const useNativeUpload = this.usesNativeHttpBridge();

            if (useNativeUpload && file) {
                // iOS: Convert file to base64 and send as JSON
                const sanitizedFileName = sanitizeFileName(file.name);
                const base64File = await this.fileToBase64(file);

                const jsonBody: ECGRequestForm = {
                    message: requestData.message,
                    expertiseArea: requestData.speciality, // Backend expects expertiseArea (camelCase)
                    requestType: requestData.requestType,
                    patientId: requestData.patientId,
                    fileB64: base64File, // Backend expects fileB64, not file
                    fileName: sanitizedFileName,
                    fileType: file.type,
                    personalHistory: requestData.personalHistory,
                    familyHistory: requestData.familyHistory,
                    cardiovascularRiskFactors: requestData.cardiovascularRiskFactors,
                    regularMedications: requestData.regularMedications,
                    functionalSymptoms: requestData.functionalSymptoms,
                    clinicalSigns: requestData.clinicalSigns
                };

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });
                // Backend reads `send` as a query param (@RequestParam), not from the JSON body
                const params = new HttpParams().set('send', 'true');

                const response = await this._httpClient
                    .put<IMedicalRequest>(`aplt_backend/api/requests/ECG/${id}`, jsonBody, { headers, params })
                    .toPromise();
                if (!response) throw new Error('No response received from server');

                const sentRequest = new MedicalRequest(response);
                if (this._currentRequest()?.id === id) {
                    this._currentRequest.set(cloneDeep(sentRequest));
                }
                await this.loadRequests(this._currentFilter());
                return sentRequest;
            } else {
                // Android/Web: Use FormData (existing behavior)
                const formData = new FormData();
                if (file) {
                    const sanitizedFile = new File([file], sanitizeFileName(file.name), { type: file.type });
                    formData.append('file', sanitizedFile);
                }

                let params = new HttpParams()
                    .set('message', requestData.message)
                    .set('speciality', requestData.speciality)
                    .set('requestType', requestData.requestType)
                    .set('patientId', requestData.patientId)
                    .set('send', 'true'); // Send immediately

                // Add optional fields if provided
                if (requestData.personalHistory) params = params.set('personalHistory', requestData.personalHistory);
                if (requestData.familyHistory) params = params.set('familyHistory', requestData.familyHistory);
                if (requestData.cardiovascularRiskFactors) params = params.set('cardiovascularRiskFactors', requestData.cardiovascularRiskFactors);
                if (requestData.regularMedications) params = params.set('regularMedications', requestData.regularMedications);
                if (requestData.functionalSymptoms) params = params.set('functionalSymptoms', requestData.functionalSymptoms);
                if (requestData.clinicalSigns) params = params.set('clinicalSigns', requestData.clinicalSigns);

                // Use HttpClient - the interceptor handles FormData Content-Type correctly
                const response = await this._httpClient
                    .put<IMedicalRequest>(`aplt_backend/api/requests/ECG/${id}`, formData, { params })
                    .toPromise();
                if (!response) throw new Error('No response received from server');

                const sentRequest = new MedicalRequest(response);
                if (this._currentRequest()?.id === id) {
                    this._currentRequest.set(cloneDeep(sentRequest));
                }
                await this.loadRequests(this._currentFilter());
                return sentRequest;
            }
        } catch (error) {
            console.error('Error updating and sending ECG request:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to update and send request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    // Utility methods
    setCurrentRequest(request: MedicalRequest | null): void {
        this._currentRequest.set(request ? cloneDeep(request) : null);
    }

    async applyFilter(filter: IRequestFilter): Promise<void> {
        const newFilter = { sort: 'zcreationTimestamp:desc', ...this._currentFilter(), ...filter };
        await this.loadRequests(newFilter);
    }

    async clearFilters(): Promise<void> {
        await this.loadRequests({ sort: 'zcreationTimestamp:desc' });
    }

    clearMedicalOpinions(): void {
        this._currentRequestOpinions.set([]);
        this._opinionsError.set(null);
    }

    /**
     * Clears all request data (for logout)
     */
    clearData(): void {
        this._allRequests.set([]);
        this._currentRequest.set(null);
        this._error.set(null);
        this._isLoading.set(false);
        this._currentFilter.set({ sort: 'zcreationTimestamp:desc' });
        this._pagination.set(null);
        this._currentRequestOpinions.set([]);
        this._opinionsError.set(null);
        this._opinionsLoading.set(false);
    }

    async getRequestByIdWithOpinions(id: string, loadOpinions: boolean = true, forceReload: boolean = false): Promise<MedicalRequest | null> {
        const request = await this.getRequestById(id, forceReload);
        if (request && loadOpinions) {
            try {
                await this.loadMedicalOpinions(id);
            } catch (error) {
                console.warn('Failed to load medical opinions for request:', error);
            }
        }
        return request;
    }

    hasNewOpinions(request: MedicalRequest): boolean {
        return request.status === 'COMPLETED' && !request.isRead;
    }

    getMedicalOpinionById(opinionId: string): any {
        return this._currentRequestOpinions().find(op => op.id === opinionId) || null;
    }

    async refreshMedicalOpinions(requestId: string): Promise<void> {
        await this.loadMedicalOpinions(requestId);
    }

    /**
     * Load patient data for request - using cache first approach
     */
    private async loadPatientDataForRequest(request: MedicalRequest): Promise<MedicalRequest> {
        if (!request.patient && request.patientId) {
            let patientData = this._patientCacheService.getPatient(request.patientId);
            
            if (!patientData) {
                try {
                    patientData = await this._patientService.getPatientById(request.patientId);
                    if (patientData) {
                        this._patientCacheService.setPatient(patientData);
                    }
                } catch (error) {
                    console.warn(`Failed to load patient data for request ${request.id}:`, error);
                }
            }
            
            if (patientData) {
                const updatedRequest = request.clone();
                updatedRequest.patient = patientData.toDTO();
                return updatedRequest;
            }
        }
        return request;
    }

    /**
    * Send an existing draft request (convert draft to sent using send=true)
    * iOS: Uses JSON format
    * Android/Web: Uses FormData (existing behavior)
    */
    async sendDraftRequest(id: string): Promise<MedicalRequest> {
        const totalStartTime = performance.now();
        console.log(`[PERF] sendDraftRequest START - RequestId: ${id}, Platform: ${this.usesNativeHttpBridge() ? 'Native' : 'Web'}`);
        
        this._isLoading.set(true);
        this._error.set(null);
    
        try {
            // Get current request to extract its data
            const currentRequest = this._allRequests().find(r => r.id === id || r.entityId === id);
            if (!currentRequest) {
                throw new Error('Request not found');
            }
    
            if (currentRequest.status !== 'DRAFT') {
                throw new Error('Only draft requests can be sent');
            }
    
            // Extract request data and call API directly with send=true
            const requestData = this.extractRequestDataFromCurrent(currentRequest);
            const useNativeUpload = this.usesNativeHttpBridge();
            
            if (useNativeUpload) {
                // iOS: Send as JSON with fileB64 (backend requires it even for updates)
                // NOTE: Backend requires fileB64, so we must download and convert the file
                let fileB64: string | undefined;
                let fileName: string | undefined;
                let fileType: string | undefined;
                
                const fileItems = currentRequest.fileItems;
                if (fileItems && fileItems.length > 0) {
                    // Get the first file item (ECG requests typically have one file)
                    const fileItem = fileItems[0];
                    if (fileItem && fileItem.value) {
                        try {
                            // Download file and convert to base64 (required by backend)
                            const fileData = await this.fileIdToBase64(fileItem.value);
                            fileB64 = fileData.base64;
                            fileName = fileData.fileName;
                            fileType = fileData.fileType;
                        } catch (error) {
                            console.warn('Could not download file for draft send, continuing without file:', error);
                        }
                    }
                }

                // Backend's ECGRequestForm requires these fields to be present in the JSON
                // even when there's no file to (re)send - undefined fields get dropped by
                // JSON.stringify and the backend rejects the request as malformed.
                if (!fileB64) fileB64 = '';
                if (!fileName) fileName = '';
                if (!fileType) fileType = '';

                const jsonBody: ECGRequestForm = {
                    message: requestData.message,
                    expertiseArea: requestData.speciality, // Backend expects expertiseArea (camelCase)
                    requestType: requestData.requestType,
                    patientId: requestData.patientId,
                    fileB64: fileB64, // Backend requires fileB64 even for updates
                    fileName: fileName,
                    fileType: fileType,
                    // Note: send parameter is in query params, not body (like Android/Web)
                    personalHistory: requestData.personalHistory,
                    familyHistory: requestData.familyHistory,
                    cardiovascularRiskFactors: requestData.cardiovascularRiskFactors,
                    regularMedications: requestData.regularMedications,
                    functionalSymptoms: requestData.functionalSymptoms,
                    clinicalSigns: requestData.clinicalSigns
                };

                const jsonSizeMB = (JSON.stringify(jsonBody).length / (1024 * 1024)).toFixed(2);
                console.log(`[PERF] sendDraftRequest iOS - JSON payload size: ${jsonSizeMB}MB`);

                // Use query params for send parameter (like Android/Web) - backend reads it from query params
                const params = new HttpParams().set('send', 'true');

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });

                const requestStartTime = performance.now();
                const response = await this._httpClient
                    .put<IMedicalRequest>(`aplt_backend/api/requests/ECG/${id}`, jsonBody, { headers, params })
                    .toPromise();
                const requestTime = performance.now() - requestStartTime;
                console.log(`[PERF] sendDraftRequest iOS - Backend request time: ${requestTime.toFixed(2)}ms`);
                
                if (!response) throw new Error('No response received from server');

                console.log('[iOS] Backend response after sending draft:', {
                    entityId: response.entityId,
                    status: response.status
                });

                const sentRequest = new MedicalRequest(response);
                
                const patientDataStartTime = performance.now();
                // OPTIMIZATION: Use existing patient data from currentRequest if available (faster)
                // Only load patient data if not already present in response
                let sentRequestWithPatient: MedicalRequest;
                if (sentRequest.patient || !currentRequest.patient) {
                    // Patient data already in response, or we don't have it cached - load it
                    sentRequestWithPatient = await this.loadPatientDataForRequest(sentRequest);
                } else {
                    // Use cached patient data from currentRequest (much faster)
                    const updatedRequest = sentRequest.clone();
                    updatedRequest.patient = currentRequest.patient;
                    sentRequestWithPatient = updatedRequest;
                }
                const patientDataTime = performance.now() - patientDataStartTime;
                console.log(`[PERF] sendDraftRequest iOS - Patient data load: ${patientDataTime.toFixed(2)}ms`);
                
                console.log('[iOS] Request after patient data load:', {
                    id: sentRequestWithPatient.id,
                    status: sentRequestWithPatient.status,
                    entityId: sentRequestWithPatient.entityId
                });
                
                // IMMEDIATELY update the list optimistically (don't wait for reload)
                const requests = this._allRequests();
                let found = false;
                const updatedRequests = requests.map(r => {
                    if (r.id === id || r.entityId === id) {
                        found = true;
                        console.log('[iOS] Updating request in list:', {
                            oldStatus: r.status,
                            newStatus: sentRequestWithPatient.status,
                            oldId: r.id,
                            newId: sentRequestWithPatient.id
                        });
                        return sentRequestWithPatient;
                    }
                    return r;
                });
                
                if (!found) {
                    console.warn('[iOS] Request not found in list for update:', {
                        searchId: id,
                        requestId: sentRequestWithPatient.id,
                        requestEntityId: sentRequestWithPatient.entityId,
                        listSize: requests.length
                    });
                }
                
                this._allRequests.set(updatedRequests);
                
                // Update current request if it's the same one
                if (this._currentRequest()?.id === id || this._currentRequest()?.entityId === id) {
                    this._currentRequest.set(cloneDeep(sentRequestWithPatient));
                }
                
                // Reload list in background without blocking (fire and forget)
                this.loadRequests(this._currentFilter()).catch(error => {
                    console.warn('Background reload after sending request failed:', error);
                });
                
                const totalTime = performance.now() - totalStartTime;
                console.log(`[PERF] sendDraftRequest iOS END - Total time: ${totalTime.toFixed(2)}ms (Backend: ${requestTime.toFixed(2)}ms, Patient: ${patientDataTime.toFixed(2)}ms)`);
                
                return sentRequestWithPatient;
            } else {
                // Android/Web: Use FormData (existing behavior)
                const formDataStartTime = performance.now();
                const formData = new FormData();
                let params = new HttpParams()
                    .set('message', requestData.message)
                    .set('speciality', requestData.speciality)
                    .set('requestType', requestData.requestType)
                    .set('patientId', requestData.patientId)
                    .set('send', 'true'); // KEY: send=true to send the draft
    
                // Add optional fields if provided
                if (requestData.personalHistory) params = params.set('personalHistory', requestData.personalHistory);
                if (requestData.familyHistory) params = params.set('familyHistory', requestData.familyHistory);
                if (requestData.cardiovascularRiskFactors) params = params.set('cardiovascularRiskFactors', requestData.cardiovascularRiskFactors);
                if (requestData.regularMedications) params = params.set('regularMedications', requestData.regularMedications);
                if (requestData.functionalSymptoms) params = params.set('functionalSymptoms', requestData.functionalSymptoms);
                if (requestData.clinicalSigns) params = params.set('clinicalSigns', requestData.clinicalSigns);
    
                const formDataTime = performance.now() - formDataStartTime;
                console.log(`[PERF] sendDraftRequest Android/Web - FormData preparation: ${formDataTime.toFixed(2)}ms`);
    
                // Use HttpClient - the interceptor handles FormData Content-Type correctly
                const requestStartTime = performance.now();
                const response = await this._httpClient
                    .put<IMedicalRequest>(`aplt_backend/api/requests/ECG/${id}`, formData, { params })
                    .toPromise();
                const requestTime = performance.now() - requestStartTime;
                console.log(`[PERF] sendDraftRequest Android/Web - Backend request time: ${requestTime.toFixed(2)}ms`);
                
                if (!response) throw new Error('No response received from server');
    
                console.log('[Android/Web] Backend response after sending draft:', {
                    entityId: response.entityId,
                    status: response.status
                });
    
                const sentRequest = new MedicalRequest(response);
                
                const patientDataStartTime = performance.now();
                // OPTIMIZATION: Use existing patient data from currentRequest if available (faster)
                // Only load patient data if not already present in response
                let sentRequestWithPatient: MedicalRequest;
                if (sentRequest.patient || !currentRequest.patient) {
                    // Patient data already in response, or we don't have it cached - load it
                    sentRequestWithPatient = await this.loadPatientDataForRequest(sentRequest);
                } else {
                    // Use cached patient data from currentRequest (much faster)
                    const updatedRequest = sentRequest.clone();
                    updatedRequest.patient = currentRequest.patient;
                    sentRequestWithPatient = updatedRequest;
                }
                const patientDataTime = performance.now() - patientDataStartTime;
                console.log(`[PERF] sendDraftRequest Android/Web - Patient data load: ${patientDataTime.toFixed(2)}ms`);
                
                console.log('[Android/Web] Request after patient data load:', {
                    id: sentRequestWithPatient.id,
                    status: sentRequestWithPatient.status,
                    entityId: sentRequestWithPatient.entityId
                });
                
                // IMMEDIATELY update the list optimistically (don't wait for reload)
                const requests = this._allRequests();
                let found = false;
                const updatedRequests = requests.map(r => {
                    if (r.id === id || r.entityId === id) {
                        found = true;
                        console.log('[Android/Web] Updating request in list:', {
                            oldStatus: r.status,
                            newStatus: sentRequestWithPatient.status,
                            oldId: r.id,
                            newId: sentRequestWithPatient.id
                        });
                        return sentRequestWithPatient;
                    }
                    return r;
                });
                
                if (!found) {
                    console.warn('[Android/Web] Request not found in list for update:', {
                        searchId: id,
                        requestId: sentRequestWithPatient.id,
                        requestEntityId: sentRequestWithPatient.entityId,
                        listSize: requests.length
                    });
                }
                
                this._allRequests.set(updatedRequests);
                
                // Update current request if it's the same one
                if (this._currentRequest()?.id === id || this._currentRequest()?.entityId === id) {
                    this._currentRequest.set(cloneDeep(sentRequestWithPatient));
                }
                
                // Reload list in background without blocking (fire and forget)
                this.loadRequests(this._currentFilter()).catch(error => {
                    console.warn('Background reload after sending request failed:', error);
                });
                
                const totalTime = performance.now() - totalStartTime;
                console.log(`[PERF] sendDraftRequest Android/Web END - Total time: ${totalTime.toFixed(2)}ms (FormData: ${formDataTime.toFixed(2)}ms, Backend: ${requestTime.toFixed(2)}ms, Patient: ${patientDataTime.toFixed(2)}ms)`);
                
                return sentRequestWithPatient;
            }
            
        } catch (error) {
            const totalTime = performance.now() - totalStartTime;
            console.error(`[PERF] sendDraftRequest ERROR - Failed after ${totalTime.toFixed(2)}ms:`, error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to send request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
    * Extract request data from current request for sending drafts
    */
    private extractRequestDataFromCurrent(request: MedicalRequest): IECGRequestUpdate {
        const content = request.content;
        
        return {
            message: content.getValue('message') || '',
            speciality: request.speciality,
            requestType: request.requestType,
            patientId: request.patientId,
            personalHistory: content.getValue('personalHistory'),
            familyHistory: content.getValue('familyHistory'),
            cardiovascularRiskFactors: content.getValue('cardiovascularRiskFactors'),
            regularMedications: content.getValue('regularMedications'),
            functionalSymptoms: content.getValue('functionalSymptoms'),
            clinicalSigns: content.getValue('clinicalSigns')
        };
    }
}